import { format, parseISO } from 'date-fns'
import { cs } from 'date-fns/locale'
import { routesURLs } from '../Routes'

export const classes = (classString: string, classes?: { [key: string]: boolean }) => {
  const classesList = []
  for (const classKey in classes) {
    if (classes.hasOwnProperty(classKey)) {
      const condition = classes[classKey]
      if (condition) {
        classesList.push(classKey)
      }
    }
  }
  return `${classString}${classesList.length && classString ? ' ' : ''}${classesList.join(' ')}`
}

type ErrorType = { response: { data: { errors: { [param: string]: string[] }; msg: string } } }

export const transformErrors = (error: ErrorType) => {
  const data = error.response.data.errors
  const errorList = Object.keys(data)
  const errors: { [key: string]: string } = {}
  if (errorList.length) {
    errorList.forEach((item) => {
      errors[item] = data[item][0]
    })
  }
  return errors
}

export const pad = function (a: number) {
  return a < 10 ? '0' + a : a
}

export const formateNumber = (number: number, round: number) => {
  let decimals = Math.pow(10, round)
  const adjNumber = Math.round(number * decimals) / decimals
  return adjNumber
    .toString()
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const sessionStore = {
  get: (item: string) => {
    return JSON.parse(sessionStorage.getItem(item) || JSON.stringify(''))
  },
  set: (item: string, values: any) => {
    sessionStorage.setItem(item, JSON.stringify(values))
  }
}

export const courseTermDescription = ({
  dateFrom,
  dateTo,
  price,
  lessonDate
}: {
  dateFrom: string
  dateTo: string
  lessonDate?: string
  price?: number
}) => {
  const selectedDateFrom = new Date(dateFrom)
  const selectedDateTo = new Date(dateTo)
  return `${price ? formateNumber(price, 0) + ' Kč · ' : ''}${
    lessonDate
      ? `${format(new Date(lessonDate), 'EEEE · p  · ', {
          locale: cs
        })}`
      : ''
  }${format(selectedDateFrom, 'd. MMMM yyyy', {
    locale: cs
  })} - ${format(selectedDateTo, 'd. MMMM yyyy', {
    locale: cs
  })}`
}

export const testTermDescription = ({
  dateFrom,
  timeFrom,
  price
}: {
  dateFrom: string
  timeFrom: string
  price?: number
}) => {
  const output: string[] = []

  if (price) {
    output.push(`${formateNumber(price, 0)} Kč`)
  }

  try {
    const parsed = parseISO(dateFrom)

    if (timeFrom && /^\d{2}:\d{2}$/.test(timeFrom.trim())) {
      const [hours, minutes] = timeFrom.split(':').map(Number)
      parsed.setHours(hours)
      parsed.setMinutes(minutes)
    }

    const formatted = format(parsed, timeFrom ? 'd. MMMM yyyy HH:mm' : 'd. MMMM yyyy', {
      locale: cs
    })

    output.push(formatted)
  } catch {
    output.push('Neplatné datum')
  }

  return output.join(' · ')
}

export const testDateTermDescription = ({
  dateFrom,
  price
}: {
  dateFrom: string
  price?: number
}) => {
  const output: string[] = []

  if (price) {
    output.push(`${formateNumber(price, 0)} Kč`)
  }

  try {
    const parsed = parseISO(dateFrom)

    const formatted = format(parsed, 'd. MMMM yyyy', {
      locale: cs
    })

    output.push(formatted)
  } catch {
    output.push('Neplatné datum')
  }

  return output.join(' · ')
}

export const getReadableDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString)
    return format(date, 'dd/MM/yyyy')
  } catch {
    return dateString
  }
}

export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`
  const parts: string[] = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()!.split(';').shift()
}

export const setCookie = (name: string, value: string, days: number) => {
  const d = new Date()
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
  var expires = 'expires=' + d.toUTCString()
  document.cookie = `${name}=${value};${days ? expires : ''}; path=/`
}

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

/**
 * Reads the device-token cookie, storing a new UUID first when it is missing.
 * Cookiebot deletes the cookie mid-session when a visitor declines consent.
 */
export const getDeviceToken = () => {
  const stored = getCookie('device-token')
  if (stored) return stored

  const token = (String(1e7) + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16)
  )
  setCookie('device-token', token, 400)
  return token
}

export const plural = (count: number, singular: string, plural: string, plural5more: string) => {
  if (count === 1) {
    return singular
  } else if (count > 1 && count < 5) {
    return plural
  } else {
    return plural5more
  }
}

type DataLayerObject = {
  [key: string]: any
}

export const pushDataLayer = (payload: DataLayerObject) => {
  if (typeof window === 'undefined') return
  ;(window as any).dataLayer = (window as any).dataLayer || []
  ;(window as any).dataLayer.push(payload)
}

/**
 * Converts raw test scores into bell curve chart data.
 * @param {number[]} scores - Array of raw scores (0-50).
 * @param {number} maxPoints - Max possible points (default: 50)
 * @param {number} points - Number of percentile points (default: 51)
 * @returns {Array<{ points: number, users: number }>}
 */
export const buildBellCurveData = (
  scores: number[],
  maxPoints: number = 50,
  points: number = 51
): Array<{ points: number; users: number }> => {
  // Guard clauses
  if (!Array.isArray(scores) || scores.length === 0 || maxPoints <= 0) return []

  // clamp to range
  const values = scores
    .filter((s) => Number.isFinite(s))
    .map((s) => Math.max(0, Math.min(maxPoints, s)))

  if (values.length === 0) return []

  // Silverman's rule-of-thumb bandwidth for KDE
  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance = values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) / values.length
  const std = Math.sqrt(variance)
  const n = values.length
  const bandwidth = Math.max(1, 1.06 * (std || 1) * Math.pow(n, -1 / 5)) // ensure minimum smoothing

  // Gaussian kernel
  const gaussian = (x: number) => Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI)

  // Evaluation grid: 0..maxPoints inclusive, default 51 points
  const count = Math.max(3, points) // at least 3 points to avoid degenerate axis
  const grid: number[] = Array.from({ length: count }, (_, i) => (i * maxPoints) / (count - 1))

  // KDE estimate at each grid point
  const densities = grid.map((x) => {
    const sum = values.reduce((acc, v) => acc + gaussian((x - v) / bandwidth), 0)
    // Normalize by n and bandwidth so area roughly integrates to 1 in continuous sense
    return sum / (n * bandwidth)
  })

  // Scale densities to a convenient magnitude for chart (e.g., sum to sample size)
  const scale = n / densities.reduce((a, b) => a + b, 0)
  const scaled = densities.map((d) => d * scale)

  return grid.map((p, i) => ({ points: Math.round(p), users: scaled[i] }))
}

export const getBannerCourseLink = (
  courseId: number,
  courseType: string,
  courseKey?: string,
  subjectId?: number,
  lessonClassId?: number,
  cityId?: number
): string => {
  switch (courseType) {
    case 'online_course':
      return `${routesURLs.onlineCourses}/${subjectId}/${lessonClassId}`
    case 'onsite_course':
      return `${routesURLs.personalCourses}/${cityId}?courseId=${courseId}`
    case 'online_mock_exam_course':
      return `${routesURLs.onlineMockExamCourses}/${courseId}`
    case 'onsite_test':
      return `${routesURLs.personalTests}/2?courseId=${encodeURIComponent(courseKey || '')}`
    default:
      return '/'
  }
}
